/**
 * 品目の進み具合(段階と進捗率)。
 *
 * 一覧で「その品目が今どこにいるか」を出すためのもの。
 * 状態(status)は draft / reviewing / approved の3つしか無く、実際の運用では
 * 「レビュー中」に何週間も居座る。**中で何割終わっているかが見えない**ため、
 * 21品を並行して進めると、どれに手を付ければよいか分からなくなる。
 *
 * 段階は次の順に進む:
 *   情報登録中 → (生成中) → レビュー待ち → レビュー中 x% → 再生成待ち
 *   → レビュー承認待ち → レビュー承認済み
 *
 * **ここでは何も数え直さない。** 分母(残っている確認事項)は CLI が出した
 * review_questions.json をワーカーが数えて dish.review へ書いた値、分子は
 * 回答のトリガ(functions/progress.mjs)が数えた値で、この関数は段階に畳むだけ。
 * 画面側で質問を組み立て直すと CLI と問いが食い違う(lib/review.ts 冒頭と同じ理由)。
 */
import type { DishDoc, FailureKind, JobType } from "./types";

export type Stage =
  /** ①素材を登録している最中。まだマニュアルが無い */
  | "registering"
  /** 生成・反映・翻訳のいずれかを実行中 */
  | "generating"
  /** 直近の実行が失敗した(人が気付いて再実行する必要がある) */
  | "failed"
  /** ②生成済みで、確認事項にまだ1件も答えていない */
  | "awaiting"
  /** ④回答の途中。保留(空欄で保存)が残っているあいだもここ */
  | "reviewing"
  /** 回答は揃ったが、まだ本文へ反映していない(⑤の実行待ち) */
  | "regenerating"
  /** 残っている確認事項が無く、あとは料理長の承認だけ(⑥の実行待ち) */
  | "approving"
  /** ⑥承認済み。以後この内容がゴールデン(正) */
  | "approved";

export const STAGE_LABEL: Record<Stage, string> = {
  registering: "情報登録中",
  generating: "生成中",
  failed: "実行に失敗",
  awaiting: "レビュー待ち",
  reviewing: "レビュー中",
  regenerating: "再生成待ち",
  approving: "レビュー承認待ち",
  approved: "レビュー承認済み",
};

/** 段階の意味と、次に誰が何をするか。一覧の凡例と行のタイトルに出す */
export const STAGE_HELP: Record<Stage, string> = {
  registering: "動画とレシピ・メモを登録し、「マニュアルを生成」を実行してください。",
  generating: "実行中です。数分〜十数分かかります。終わると自動で次の段階に進みます。",
  failed: "直近の実行が失敗しました。品目の画面で理由を確認し、もう一度実行してください。",
  awaiting: "マニュアルができました。「レビュー・回答」から確認事項に答えてください。",
  reviewing: "確認事項への回答が途中です。保留(空欄で保存)のままでは承認できません。",
  regenerating: "確認事項に答え終わりました。「回答を本文に反映」を実行してください。",
  approving: "料理長が「承認する」を実行すると確定します。",
  approved: "承認済みです。この内容が評価の基準になります。",
};

/** 実行中の見出し。何を待っているのかで言葉を変える(ただ「処理中」では待てない) */
export const RUNNING_LABEL: Record<JobType, string> = {
  analyze: "マニュアル生成中",
  render: "組版中",
  compare: "比較中",
  resolve: "回答を反映中",
  approve: "承認処理中",
  translate: "中国語版を生成中",
  "link-prep": "仕込みを紐づけ中",
  "prep-doc": "仕込みをレシピから取り込み中",
};

/**
 * 失敗の見出し。JOB_TYPE_LABEL は「マニュアルを生成」のような動詞句で、
 * 「〜に失敗」と繋ぐと文にならない（実際「回答を本文に反映に失敗」と出た）。
 */
export const FAILED_LABEL: Record<JobType, string> = {
  analyze: "マニュアルの生成に失敗",
  render: "組版に失敗",
  compare: "比較に失敗",
  resolve: "回答の反映に失敗",
  approve: "承認に失敗",
  translate: "中国語版の生成に失敗",
  "link-prep": "仕込みの紐づけに失敗",
  "prep-doc": "仕込みの取り込みに失敗",
};

/**
 * 再実行では直らない失敗の見出し。「失敗(再実行が要ります)」と出すと同じ操作を繰り返させる
 * (揚げマナガツオの「揚げにんにく」はレシピにもメモにも作り方が無く、何度取り込んでも同じだった)
 */
export const FAILED_KIND_BADGE: Record<FailureKind, string> = {
  "prep-doc-not-found": "対応の選択待ち",
};

export const FAILED_KIND_LABEL: Record<FailureKind, string> = {
  "prep-doc-not-found": "「{name}」の作り方が資料にありません。扱いを選んでください",
};

export interface Progress {
  stage: Stage;
  /**
   * レビューの進捗率(0〜100)。
   * 分母が無い(確認事項ゼロ)ときと、進捗を計測していない品目では null。
   * **null と 0 は違う。** 0 は「1件も答えていない」、null は「数えられない」。
   */
  percent: number | null;
  /** 解決済み(累計) + 回答済みで未反映。分子 */
  done: number;
  /** これまでに出た確認事項の延べ数(解決済み + 残り)。分母 */
  total: number;
  /** done のうち保留(空欄で記録)の数 */
  held: number;
  /** いま残っている確認事項の数(分母ではなく「あと何問か」) */
  remaining: number;
  /** generating / failed のときの対象ジョブ */
  jobType: JobType | null;
  /** failed のうち、再実行では直らないものの種類(FailedJob.kind)。それ以外は null */
  failedKind: FailureKind | null;
}

/** 進捗率。分母が0なら null(0% と出すと「全く進んでいない」に見える) */
export function percentOf(done: number, total: number): number | null {
  if (total <= 0) return null;
  return Math.round((Math.min(done, total) / total) * 100);
}

export function progressOf(dish: DishDoc): Progress {
  const r = dish.review ?? null;
  const remaining = r?.open ?? 0;
  const held = r?.held ?? 0;
  const answered = r?.answered ?? 0;
  // 解決済みの累計(ワーカーが反映のたびに足す)。
  //
  // 率の分母を「いま残っている確認事項」にすると、反映のたびに分母も分子も減り、
  // 直後は必ず 0% になる(16問→1問まで解いた品目が「0%」と出た)。
  // 延べ数(解決済み + 残り)を分母にすれば、反映しても率が戻らない。
  //
  // 分子に保留(空欄で保存)は入れない。保留は未解消で承認を止めるため、
  // 数えると承認できない品目が 100% と出る。件数だけ別に持つ。
  const resolved = r?.resolved ?? 0;
  const total = resolved + remaining;
  const done = Math.min(resolved + answered, total);
  const base = { percent: percentOf(done, total), done, total, held, remaining, jobType: null, failedKind: null };
  const failed = dish.failedJob
    ? { stage: "failed" as const, jobType: dish.failedJob.type, failedKind: dish.failedJob.kind ?? null }
    : null;

  // 承認済みが最優先。承認後に中国語版を生成しても「生成中」へ戻して見せない
  // (確定した品目が未確定に見えると、評価の基準が揺らいでいるように読める)
  if (dish.isGolden || dish.status === "approved") {
    return { ...base, stage: "approved", percent: null };
  }

  // 実行中は、進捗率より「待てば終わる」ことの方が知りたい情報
  if (dish.activeJob) return { ...base, stage: "generating", jobType: dish.activeJob.type };

  if (!dish.currentAnalysisPath) {
    // 生成前の失敗(動画が壊れている等)は、放っておくと「情報登録中」のまま止まる
    if (failed) return { ...base, ...failed };
    return { ...base, stage: "registering" };
  }

  // 進捗を計測していない品目(この機能より前に生成したもの)。
  // 0% と出すと「誰も手を付けていない」と誤読させるので、率は伏せて段階だけ出す
  if (!r) return { ...base, stage: "reviewing", percent: null };

  // マニュアルはあるが直近の実行が失敗している。進捗率は出したまま失敗を優先して見せる
  if (failed) return { ...base, ...failed };

  if (remaining === 0) return { ...base, stage: "approving" };
  // 保留だけでも、前に解決した分があっても「手は付いている」。レビュー待ち(未着手)には戻さない
  if (answered === 0 && held === 0 && resolved === 0) return { ...base, stage: "awaiting" };
  // 残っている質問すべてに回答済み(保留を除く)なら、次は反映
  if (answered >= remaining) return { ...base, stage: "regenerating" };
  return { ...base, stage: "reviewing" };
}

/** 一覧の凡例に出す順。段階の並び順そのもの */
export const STAGE_FLOW: Stage[] = [
  "registering",
  "awaiting",
  "reviewing",
  "regenerating",
  "approving",
  "approved",
];
